// src/pages/AssessmentPage.js

import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import './AssessmentPage.css';

function AssessmentPage() {
  const [surveys, setSurveys] = useState([]);
  const [activeTab, setActiveTab] = useState('active'); // 'active', 'incomplete', 'archive'
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');
  
  // Nickname editing state
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [nicknameText, setNicknameText] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterFeedback, setFilterFeedback] = useState(''); // '', 'negative', 'best'

  // Refuge Ai Logo URL
  const logoUrl = 'https://firebasestorage.googleapis.com/v0/b/steps4inclusiveai.firebasestorage.app/o/site-assets%2Frefugeai%2Frefugeai_logo01.jpg?alt=media&token=52a9fde1-198e-4cbb-a4e4-e116e2829bd2';

  useEffect(() => {
    const surveysRef = collection(db, 'surveys');
    
    const unsubscribe = onSnapshot(
      surveysRef, 
      (snapshot) => {
        const loadedSurveys = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        // Sort by newest first
        loadedSurveys.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setSurveys(loadedSurveys);
      },
      (error) => {
        // Keeping this error log prevents silent failures in production
        console.error("Firebase Error Fetching Surveys: ", error.message);
      }
    );

    return () => unsubscribe();
  }, []);

  // --- Calculations & Formatting ---
  const activeSurveys = surveys.filter(s => s.status === 'active');
  
  const calculateAverage = (field) => {
    if (activeSurveys.length === 0) return 0;
    const sum = activeSurveys.reduce((acc, s) => acc + (Number(s[field]) || 0), 0);
    return (sum / activeSurveys.length).toFixed(1);
  };

  const urgencyScore = Number(calculateAverage('q2_urgency'));
  const relevanceScore = Number(calculateAverage('q3_relevance'));
  const progressScore = Number(calculateAverage('q4_progress'));
  const supportScore = Number(calculateAverage('q5_support'));
  
  const mainScore = activeSurveys.length > 0 
    ? ((urgencyScore + relevanceScore + progressScore + supportScore) / 4).toFixed(1) 
    : 0;

  // Helper to create a friendly display ID (e.g., Survey ZGCXDAZ)
  const formatDisplayId = (id, state) => {
    const shortId = id.substring(0, 5).toUpperCase();
    const stateSuffix = (state && state !== 'user declined to answer') ? state.toUpperCase() : '';
    return `Survey ${shortId}${stateSuffix}`;
  };

  // --- Handlers ---
  const handleCopyLink = () => {
    const surveyUrl = `${window.location.origin}/survey`;
    navigator.clipboard.writeText(surveyUrl);
    setCopyMessage('Link copied!');
    setTimeout(() => setCopyMessage(''), 3000);
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleUpdateSurvey = async (id, updates) => {
    try {
      await updateDoc(doc(db, 'surveys', id), updates);
      if (selectedSurvey && selectedSurvey.id === id) {
        setSelectedSurvey({ ...selectedSurvey, ...updates });
      }
    } catch (error) {
      console.error("Error updating survey:", error);
    }
  };

  const handleRespond = (e, id) => {
    e.stopPropagation(); // Prevent opening detail view
    handleUpdateSurvey(id, { responded: true });
  };

  const handleArchive = (e, id) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to archive this survey?")) {
      handleUpdateSurvey(id, { status: 'archived' });
      setSelectedSurvey(null);
    }
  };

  const handleSaveNickname = () => {
    handleUpdateSurvey(selectedSurvey.id, { nickname: nicknameText.trim() });
    setIsEditingNickname(false);
  };

  // --- Filtering ---
  const getFilteredSurveys = () => {
    let list = surveys.filter(s => s.status === activeTab);

    return list.filter(s => {
      const displayId = s.nickname ? s.nickname.toLowerCase() : formatDisplayId(s.id, s.state).toLowerCase();
      const term = searchTerm.toLowerCase();
      
      const matchSearch = term === '' || 
        displayId.includes(term) ||
        (s.q1_goal && s.q1_goal.toLowerCase().includes(term)) ||
        (s.q6_mostHelpful && s.q6_mostHelpful.toLowerCase().includes(term)) ||
        (s.q7_fallShort && s.q7_fallShort.toLowerCase().includes(term));

      const matchCity = filterCity === '' || (s.city && s.city.toLowerCase().includes(filterCity.toLowerCase()));
      const matchState = filterState === '' || (s.state && s.state.toLowerCase() === filterState.toLowerCase());
      
      let matchFeedback = true;
      if (filterFeedback === 'negative') {
        const avg = (Number(s.q3_relevance) + Number(s.q4_progress) + Number(s.q5_support)) / 3;
        matchFeedback = avg <= 5;
      } else if (filterFeedback === 'best') {
        const avg = (Number(s.q3_relevance) + Number(s.q4_progress) + Number(s.q5_support)) / 3;
        matchFeedback = s.isFavorited || s.isFavorited_q1 || s.isFavorited_q6 || s.isFavorited_q7 || avg >= 8;
      }

      return matchSearch && matchCity && matchState && matchFeedback;
    });
  };

  const displayedSurveys = getFilteredSurveys();
  
  // --- Print Report Logic ---
  // Ensure text is valid (not empty or default decline string)
  const isValidText = (text) => text && text !== 'user declined to answer' && text.trim() !== '';

  // Get quotes prioritized by favorited, then fallbacks, capped at max amount
  const getPrintQuotes = (qField, favField, max) => {
    const favorited = activeSurveys.filter(s => s[favField] && isValidText(s[qField]));
    const fallback = activeSurveys.filter(s => !s[favField] && isValidText(s[qField]));
    return [...favorited, ...fallback].slice(0, max);
  };

  const printQ1 = getPrintQuotes('q1_goal', 'isFavorited_q1', 2);
  const printQ7 = getPrintQuotes('q7_fallShort', 'isFavorited_q7', 2);
  
  // Q6 uses both the specific flag and the legacy general flag
  const favQ6 = activeSurveys.filter(s => (s.isFavorited_q6 || s.isFavorited) && isValidText(s.q6_mostHelpful));
  const fallbackQ6 = activeSurveys.filter(s => !(s.isFavorited_q6 || s.isFavorited) && isValidText(s.q6_mostHelpful));
  const printQ6 = [...favQ6, ...fallbackQ6].slice(0, 2);

  // --- Views ---
  if (selectedSurvey) {
    const baseDisplayId = formatDisplayId(selectedSurvey.id, selectedSurvey.state);

    return (
      <div className="dashboard-container assessment-detail-container">
        <div className="dashboard-content">
          <button className="btn btn-cancel back-btn" onClick={() => {
            setSelectedSurvey(null);
            setIsEditingNickname(false);
          }}>
            &larr; Back to List
          </button>
          
          <div className="resources-section detail-section">
            <div className="detail-header">
              
              {isEditingNickname ? (
                <div className="nickname-edit-container">
                  <input 
                    type="text" 
                    value={nicknameText} 
                    onChange={(e) => setNicknameText(e.target.value)} 
                    placeholder="Enter nickname..."
                    className="filter-input"
                    autoFocus
                  />
                  <button className="btn btn-approve" onClick={handleSaveNickname}>Save</button>
                  <button className="btn btn-cancel" onClick={() => setIsEditingNickname(false)}>Cancel</button>
                </div>
              ) : (
                <h2>
                  {selectedSurvey.nickname ? selectedSurvey.nickname : baseDisplayId}
                  {selectedSurvey.nickname && <span className="original-id-subtext">({baseDisplayId})</span>}
                </h2>
              )}

              <div className="detail-actions">
                {!isEditingNickname && (
                  <button className="btn btn-edit" onClick={() => {
                    setNicknameText(selectedSurvey.nickname || '');
                    setIsEditingNickname(true);
                  }}>
                    {selectedSurvey.nickname ? 'Edit Nickname' : 'Add Nickname'}
                  </button>
                )}
                {selectedSurvey.status !== 'archived' && (
                  <button className="btn btn-delete" onClick={(e) => handleArchive(e, selectedSurvey.id)}>
                    Archive Report
                  </button>
                )}
              </div>
            </div>

            <div className="detail-info-grid">
              <p><strong>Status:</strong> {selectedSurvey.status}</p>
              <p><strong>Location:</strong> {selectedSurvey.city}, {selectedSurvey.state}</p>
              <p><strong>Name:</strong> {selectedSurvey.name}</p>
              <p><strong>Email:</strong> {selectedSurvey.email}</p>
              <p>
                <strong>Contact Requested:</strong> {selectedSurvey.contactRequested ? 'Yes' : 'No'}
                {selectedSurvey.contactRequested && (
                  <span className="response-status">
                    ({selectedSurvey.responded ? 'Responded' : 'Needs Response'})
                  </span>
                )}
              </p>
            </div>

            <hr className="detail-divider" />

            <div className="qa-section">
              <div className="qa-item">
                <div className="qa-header">
                  <span className="q-title">1. Goal:</span>
                  <button 
                    className={`btn-favorite-inline ${selectedSurvey.isFavorited_q1 ? 'active' : ''}`}
                    onClick={() => handleUpdateSurvey(selectedSurvey.id, { isFavorited_q1: !selectedSurvey.isFavorited_q1 })}
                  >
                    {selectedSurvey.isFavorited_q1 ? '★ Favorited' : '☆ Favorite'}
                  </button>
                </div>
                <p className="a-text">{selectedSurvey.q1_goal}</p>
              </div>

              <div className="qa-row">
                <div className="qa-item score-box">
                  <span className="q-title">2. Urgency:</span>
                  <span className="a-score">{selectedSurvey.q2_urgency}/10</span>
                </div>
                <div className="qa-item score-box">
                  <span className="q-title">3. Relevance:</span>
                  <span className="a-score">{selectedSurvey.q3_relevance}/10</span>
                </div>
                <div className="qa-item score-box">
                  <span className="q-title">4. Progress:</span>
                  <span className="a-score">{selectedSurvey.q4_progress}/10</span>
                </div>
                <div className="qa-item score-box">
                  <span className="q-title">5. Support:</span>
                  <span className="a-score">{selectedSurvey.q5_support}/10</span>
                </div>
              </div>

              <div className="qa-item">
                <div className="qa-header">
                  <span className="q-title">6. Most Helpful:</span>
                  <button 
                    className={`btn-favorite-inline ${selectedSurvey.isFavorited_q6 ? 'active' : ''}`}
                    onClick={() => handleUpdateSurvey(selectedSurvey.id, { isFavorited_q6: !selectedSurvey.isFavorited_q6 })}
                  >
                    {selectedSurvey.isFavorited_q6 ? '★ Favorited' : '☆ Favorite'}
                  </button>
                </div>
                <p className="a-text">{selectedSurvey.q6_mostHelpful}</p>
              </div>

              <div className="qa-item">
                 <div className="qa-header">
                  <span className="q-title">7. Fell Short:</span>
                  <button 
                    className={`btn-favorite-inline ${selectedSurvey.isFavorited_q7 ? 'active' : ''}`}
                    onClick={() => handleUpdateSurvey(selectedSurvey.id, { isFavorited_q7: !selectedSurvey.isFavorited_q7 })}
                  >
                    {selectedSurvey.isFavorited_q7 ? '★ Favorited' : '☆ Favorite'}
                  </button>
                </div>
                <p className="a-text">{selectedSurvey.q7_fallShort}</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        
        {/* Print-Only Header Logo */}
        <div className="print-only print-header">
          <img src={logoUrl} alt="Refuge Ai Logo" className="print-logo" />
        </div>

        {/* Header & Main Menu */}
        <div className="dashboard-header-container no-print">
          <div className="dashboard-title-group">
            <h1 className="dashboard-header">Assessments</h1>
          </div>
          <div className="dashboard-tabs">
             <div className="survey-actions">
                <button className="tab-button action-btn" onClick={handleCopyLink}>
                  {copyMessage || 'Copy Survey Link'}
                </button>
                <button className="tab-button print-btn" onClick={handlePrintReport}>
                  Send Report
                </button>
             </div>
          </div>
        </div>

        {/* Score Board - Visible in App and Print */}
        <div className="score-board-container">
          <div className="main-score-card print-blue-bg">
            <span className="huge-score">{mainScore}</span>
          </div>
          <div className="sub-scores-grid">
            <div className="sub-score">
              <span className="sub-score-value">{urgencyScore}</span>
              <span className="sub-score-label">Urgency</span>
            </div>
            <div className="sub-score">
              <span className="sub-score-value">{relevanceScore}</span>
              <span className="sub-score-label">Relevance</span>
            </div>
            <div className="sub-score">
              <span className="sub-score-value">{progressScore}</span>
              <span className="sub-score-label">Progress</span>
            </div>
            <div className="sub-score">
              <span className="sub-score-value">{supportScore}</span>
              <span className="sub-score-label">Support</span>
            </div>
          </div>
        </div>

        {/* Print-Only Structured Feedback Section */}
        <div className="print-only print-feedback-section">
          <h2>Feedback</h2>
          
          {(printQ1.length === 0 && printQ6.length === 0 && printQ7.length === 0) && (
             <p>No feedback available yet.</p>
          )}

          {printQ1.length > 0 && (
            <div className="print-category">
              <h3>Goals & Context</h3>
              {printQ1.map(s => (
                <div key={`q1-${s.id}`} className="print-quote">
                  <p>"{s.q1_goal}"</p>
                  <span className="quote-author">- {s.city}, {s.state}</span>
                </div>
              ))}
            </div>
          )}

          {printQ6.length > 0 && (
            <div className="print-category">
              <h3>Most Helpful</h3>
              {printQ6.map(s => (
                <div key={`q6-${s.id}`} className="print-quote">
                  <p>"{s.q6_mostHelpful}"</p>
                  <span className="quote-author">- {s.city}, {s.state}</span>
                </div>
              ))}
            </div>
          )}

          {printQ7.length > 0 && (
            <div className="print-category">
              <h3>Room to Grow</h3>
              {printQ7.map(s => (
                <div key={`q7-${s.id}`} className="print-quote">
                  <p>"{s.q7_fallShort}"</p>
                  <span className="quote-author">- {s.city}, {s.state}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Print-Only Footer */}
        <div className="print-only print-footer">
          <p>Please contact us at <strong>www.refugeai.org</strong> to learn more about our mission.</p>
        </div>

        {/* Filters and Tabs - Hidden in Print */}
        <div className="no-print">
          <div className="assessment-tabs">
            <button className={`tab-button ${activeTab === 'active' ? 'active' : ''}`} onClick={() => setActiveTab('active')}>
              Surveys ({surveys.filter(s => s.status === 'active').length})
            </button>
            <button className={`tab-button ${activeTab === 'incomplete' ? 'active' : ''}`} onClick={() => setActiveTab('incomplete')}>
              Incomplete ({surveys.filter(s => s.status === 'incomplete').length})
            </button>
            <button className={`tab-button ${activeTab === 'archived' ? 'active' : ''}`} onClick={() => setActiveTab('archived')}>
              Archive ({surveys.filter(s => s.status === 'archived').length})
            </button>
          </div>

          <div className="dashboard-filter-bar">
              <input 
                  type="text" 
                  placeholder="Search" 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  className="filter-input search-input"
              />
              <div className="filter-dropdowns">
                  <input 
                      type="text" 
                      placeholder="City" 
                      value={filterCity} 
                      onChange={(e) => setFilterCity(e.target.value)} 
                      className="filter-input"
                  />
                  <input 
                      type="text" 
                      placeholder="State" 
                      value={filterState} 
                      onChange={(e) => setFilterState(e.target.value)} 
                      className="filter-input"
                      maxLength={2}
                  />
                  <select value={filterFeedback} onChange={(e) => setFilterFeedback(e.target.value)} className="filter-input">
                      <option value="">All Feedback</option>
                      <option value="best">Best Comments (Favorited / High Scores)</option>
                      <option value="negative">Needs Attention (Low Scores)</option>
                  </select>
              </div>
          </div>

          {/* List View */}
          <div className="resources-section">
            <h2 className="list-header">
               {activeTab === 'active' ? 'Active Surveys' : activeTab === 'incomplete' ? 'Incomplete Submissions' : 'Archived Surveys'}
            </h2>
            {displayedSurveys.length === 0 ? (
                <p style={{ padding: '10px 20px', color: '#666' }}>No surveys match your criteria.</p>
            ) : (
                <ul className="resource-list">
                    {displayedSurveys.map(s => {
                        const needsResponse = s.contactRequested && !s.responded;
                        const isGreen = !s.contactRequested || s.responded;
                        const displayId = s.nickname ? s.nickname : formatDisplayId(s.id, s.state);
                        // Show a star in the list if any part of the survey is favorited
                        const isAnyFavorited = s.isFavorited || s.isFavorited_q1 || s.isFavorited_q6 || s.isFavorited_q7;

                        return (
                          <li key={s.id} className="resource-item survey-list-item" onClick={() => {
                            setSelectedSurvey(s);
                            setIsEditingNickname(false);
                            setNicknameText(s.nickname || '');
                          }}>
                              <div className="resource-main-content">
                                  <div className="dual-status-container">
                                      <div 
                                          className={`gps-status-indicator ${isGreen ? 'gps-status-valid' : 'gps-status-missing'}`} 
                                          title={isGreen ? 'No Action Needed' : 'Contact Requested - Pending Response'}
                                      ></div>
                                  </div>
                                  <div className="resource-info">
                                      <h3 className="resource-name">{displayId}</h3>
                                      {(s.city && s.city !== 'user declined to answer') && (
                                          <span className="resource-location">
                                              {s.city}, {s.state}
                                          </span>
                                      )}
                                      {isAnyFavorited && <span className="favorite-star">★</span>}
                                  </div>
                              </div>
                              <div className="resource-actions survey-actions-col">
                                  {needsResponse && (
                                      <button className="btn btn-approve" onClick={(e) => handleRespond(e, s.id)}>
                                          Responded
                                      </button>
                                  )}
                                  {activeTab !== 'archived' && (
                                    <button className="btn btn-delete" onClick={(e) => handleArchive(e, s.id)}>
                                        Archive
                                    </button>
                                  )}
                              </div>
                          </li>
                        )
                    })}
                </ul>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default AssessmentPage;